import { createContext, useContext, useMemo, type FC, type PropsWithChildren } from 'react';

export interface DemoPanelScopeValue {
  /** The unique identifier for this demo panel. */
  panelId: string;
  /** Whether this component tree is inside the demo-panel drawer. */
  isInsideDemoPanel: boolean;
  /** Whether this panel is the topmost in the stack (visible on small screens). */
  isTopOfStack: boolean;
  /** Unregisters this panel from the stack. */
  close: () => void;
}

const DemoPanelScopeContext = createContext<DemoPanelScopeValue>({
  panelId: '',
  isInsideDemoPanel: false,
  isTopOfStack: false,
  close: () => {},
});

export interface DemoPanelScopeProviderProps extends PropsWithChildren {
  panelId: string;
  isTopOfStack: boolean;
  close: () => void;
}

/**
 * Wraps children in a demo-panel scope, providing `isTopOfStack` and `close`
 * to any descendant via `useDemoPanel()`.
 */
export const DemoPanelScopeProvider: FC<DemoPanelScopeProviderProps> = ({ children, isTopOfStack, panelId, close }) => {
  const value = useMemo<DemoPanelScopeValue>(
    () => ({ panelId, isInsideDemoPanel: true, isTopOfStack, close }),
    [panelId, isTopOfStack, close]
  );
  return <DemoPanelScopeContext.Provider value={value}>{children}</DemoPanelScopeContext.Provider>;
};

/**
 * Returns `true` when the calling component is rendered inside a `DemoPanelScopeProvider`
 * (i.e. inside the demo-panel drawer). Safe to call from any component — returns `false` by default.
 */
export const useIsInsideDemoPanel = (): boolean => useContext(DemoPanelScopeContext)?.isInsideDemoPanel ?? false;

/**
 * Returns the demo-panel scope value or default if the component is not inside the demo drawer. Safe to call anywhere.
 */
export const useDemoPanel = (): DemoPanelScopeValue => useContext(DemoPanelScopeContext);
